import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleAlert, Sparkles } from 'lucide-react';
import {
  COUNSELING_COPY,
  COUNSELING_STATUS_LABELS,
  EMPTY_STATES,
  ROLES,
} from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorNote,
  Field,
  FieldGroup,
  Input,
  SectionTitle,
  Spinner,
  Textarea,
} from '@/components/ui';
import { ApiError, api, apiData } from '@/lib/api';
import { formatDate, formatTime } from '@/lib/format';
import { useAuth } from '@/store/auth';
import type { AiWriterStatus, ChildSummary, CounselingSession, LanguageWarningRef } from '@/types';

/** 부모면담 — 명세서 §5.15 */
export function CounselingPage() {
  const membership = useAuth((s) => s.membership);
  const isParent = membership?.role === ROLES.PARENT;
  const [requesting, setRequesting] = useState(false);

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['counseling'],
    queryFn: () => apiData<CounselingSession[]>('/counseling'),
  });

  const sessions = data ?? [];

  return (
    <Screen>
      <AppBar
        title="부모면담"
        back
        right={
          isParent && (
            <Button size="sm" variant="ghost" onClick={() => setRequesting((v) => !v)}>
              {requesting ? '닫기' : '신청'}
            </Button>
          )
        }
      />

      <div className="flex flex-col gap-4 p-4">
        {requesting && <RequestForm onClose={() => setRequesting(false)} />}

        {isPending && <Spinner />}
        {isError && <ErrorNote message="불러오지 못했어요." onRetry={() => void refetch()} />}

        {!isPending && sessions.length === 0 && !requesting && (
          <EmptyState
            title={EMPTY_STATES.counseling.title}
            body={
              isParent ? EMPTY_STATES.counseling.body : '보호자가 신청하면 여기에서 확정할 수 있어요.'
            }
            action={
              isParent && (
                <Button onClick={() => setRequesting(true)}>{EMPTY_STATES.counseling.action}</Button>
              )
            }
          />
        )}

        {sessions.map((session) =>
          isParent ? (
            <ParentCard key={session.id} session={session} />
          ) : (
            <StaffCard key={session.id} session={session} />
          ),
        )}
      </div>
    </Screen>
  );
}

function slotLabel(iso: string): string {
  return `${formatDate(iso)} ${formatTime(iso)}`;
}

function invalidate(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['counseling'] });
}

/** 학부모 신청 — 희망 일시 2~3순위 (§5.15) */
function RequestForm({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const { data: children } = useQuery({
    queryKey: ['children'],
    queryFn: () => apiData<ChildSummary[]>('/children'),
  });

  const [childId, setChildId] = useState('');
  const [slots, setSlots] = useState<string[]>(['']);
  const [error, setError] = useState<string | null>(null);

  const activeChildId = childId || children?.[0]?.id || '';
  const validSlots = slots.filter(Boolean);

  const submit = useMutation({
    mutationFn: () =>
      api('/counseling', {
        method: 'POST',
        body: { childId: activeChildId, preferredSlots: validSlots },
      }),
    onSuccess: () => {
      invalidate(queryClient);
      onClose();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : '신청하지 못했어요.'),
  });

  return (
    <Card>
      <p className="mb-3 font-bold">면담 신청</p>

      {(children?.length ?? 0) > 1 && (
        <FieldGroup label="아이">
          <div className="flex flex-wrap gap-2">
            {children?.map((child) => (
              <Chip
                key={child.id}
                tone={child.id === activeChildId ? 'primary' : 'neutral'}
                selected={child.id === activeChildId}
                onClick={() => setChildId(child.id)}
              >
                {child.name}
              </Chip>
            ))}
          </div>
        </FieldGroup>
      )}

      <Field label="희망 일시" hint="편한 시간을 순서대로 최대 3개까지 적어 주세요.">
        <div className="flex flex-col gap-2">
          {slots.map((slot, index) => (
            <Input
              key={index}
              type="datetime-local"
              value={slot}
              onChange={(e) =>
                setSlots((prev) => prev.map((v, i) => (i === index ? e.target.value : v)))
              }
            />
          ))}
          {slots.length < 3 && (
            <Button variant="outline" size="sm" onClick={() => setSlots((prev) => [...prev, ''])}>
              희망 일시 추가
            </Button>
          )}
        </div>
      </Field>

      {error && <p className="mb-2 text-sm text-notice">{error}</p>}

      <div className="flex gap-2">
        <Button variant="outline" block onClick={onClose}>
          취소
        </Button>
        <Button
          block
          disabled={!activeChildId || validSlots.length === 0}
          loading={submit.isPending}
          onClick={() => submit.mutate()}
        >
          신청하기
        </Button>
      </div>
    </Card>
  );
}

function StatusHeader({ session }: { session: CounselingSession }) {
  return (
    <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
      <Chip tone={session.status === 'DONE' || session.status === 'CONFIRMED' ? 'primary' : 'secondary'}>
        {COUNSELING_STATUS_LABELS[session.status]}
      </Chip>
      <Chip tone="neutral">{session.child.name}</Chip>
      <span className="ml-auto text-sm text-ink-sub">{formatDate(session.createdAt)}</span>
    </div>
  );
}

function ParentCard({ session }: { session: CounselingSession }) {
  const queryClient = useQueryClient();
  const cancel = useMutation({
    mutationFn: () => api(`/counseling/${session.id}/cancel`, { method: 'POST' }),
    onSuccess: () => invalidate(queryClient),
  });

  return (
    <Card>
      <StatusHeader session={session} />

      {session.status === 'REQUESTED' && (
        <>
          <p className="text-base">{COUNSELING_COPY.requested}</p>
          <ul className="mt-1.5 flex flex-col gap-0.5 text-sm text-ink-sub">
            {session.preferredSlots.map((slot, i) => (
              <li key={slot}>
                {i + 1}순위 · {slotLabel(slot)}
              </li>
            ))}
          </ul>
          <Button
            className="mt-3"
            variant="outline"
            size="sm"
            loading={cancel.isPending}
            onClick={() => cancel.mutate()}
          >
            신청 취소
          </Button>
        </>
      )}

      {session.confirmedAt && session.status !== 'CANCELED' && (
        <p className="text-base font-semibold">{COUNSELING_COPY.confirmed(slotLabel(session.confirmedAt))}</p>
      )}

      {session.publicSummary && (
        <div className="mt-3 rounded-sm bg-primary-soft px-3 py-2.5">
          <p className="mb-1 text-sm font-semibold text-primary-strong">
            {COUNSELING_COPY.summaryLabel}
          </p>
          <p className="whitespace-pre-wrap text-base">{session.publicSummary}</p>
        </div>
      )}
    </Card>
  );
}

/** 교직원 — 확정과 면담록 (비공개 메모 / 공개 요약 + AI 도우미 §5.23-②) */
function StaffCard({ session }: { session: CounselingSession }) {
  const queryClient = useQueryClient();
  const [slot, setSlot] = useState(session.preferredSlots[0] ?? '');
  const [customSlot, setCustomSlot] = useState('');
  const [privateNote, setPrivateNote] = useState(session.privateNote ?? '');
  const [publicSummary, setPublicSummary] = useState(session.publicSummary ?? '');
  const [aiDraftText, setAiDraftText] = useState<string | null>(null);
  const [aiAssisted, setAiAssisted] = useState(session.aiAssisted);
  const [warnings, setWarnings] = useState<LanguageWarningRef[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const aiStatus = useQuery({
    queryKey: ['ai', 'writer', 'status'],
    queryFn: () => apiData<AiWriterStatus>('/ai/writer/status'),
  });

  const confirm = useMutation({
    mutationFn: () =>
      api(`/counseling/${session.id}/confirm`, {
        method: 'POST',
        body: { slot: new Date(customSlot || slot).toISOString() },
      }),
    onSuccess: () => invalidate(queryClient),
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : '확정하지 못했어요.'),
  });

  const save = useMutation({
    mutationFn: () =>
      apiData<{ warnings: LanguageWarningRef[] }>(`/counseling/${session.id}/notes`, {
        method: 'PATCH',
        body: { privateNote, publicSummary, aiAssisted },
      }),
    onSuccess: (result) => {
      setWarnings(result.warnings);
      setMessage(result.warnings.length > 0 ? '저장했어요. 표현을 한 번 더 살펴봐 주세요.' : '저장했어요.');
      setError(null);
      invalidate(queryClient);
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : '저장하지 못했어요.'),
  });

  const ai = useMutation({
    mutationFn: (mode: 'DRAFT' | 'POLISH') =>
      apiData<{ draft: string; warnings: LanguageWarningRef[] }>('/ai/writer/counsel-summary', {
        method: 'POST',
        body: {
          counselingId: session.id,
          mode,
          text: mode === 'POLISH' ? publicSummary : undefined,
          // 아직 저장하지 않은 메모도 초안 근거로 보낸다 — "저장 먼저" 없이 바로 동작(§5.15)
          privateNote: privateNote.trim() || undefined,
        },
      }),
    onSuccess: (result) => {
      setPublicSummary(result.draft);
      setAiDraftText(result.draft);
      setAiAssisted(true);
      setWarnings(result.warnings);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['ai', 'writer', 'status'] });
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'AI 초안을 만들지 못했어요.'),
  });

  function handleSave() {
    if (
      aiDraftText &&
      publicSummary.trim() === aiDraftText.trim() &&
      publicSummary.trim() !== '' &&
      !window.confirm('AI 초안을 수정 없이 저장하려고 해요. 면담 내용과 맞는지 확인하셨나요?')
    ) {
      return;
    }
    save.mutate();
  }

  return (
    <Card>
      <StatusHeader session={session} />

      {session.status === 'REQUESTED' && (
        <>
          <SectionTitle>일시 확정</SectionTitle>
          <div className="flex flex-col gap-2">
            {session.preferredSlots.map((preferred, i) => (
              <Chip
                key={preferred}
                tone={slot === preferred && !customSlot ? 'primary' : 'neutral'}
                selected={slot === preferred && !customSlot}
                onClick={() => {
                  setSlot(preferred);
                  setCustomSlot('');
                }}
              >
                {i + 1}순위 · {slotLabel(preferred)}
              </Chip>
            ))}
            <Field label="다른 일시로 조정">
              <Input
                type="datetime-local"
                value={customSlot}
                onChange={(e) => setCustomSlot(e.target.value)}
              />
            </Field>
          </div>
          <Button
            className="mt-2"
            block
            disabled={!slot && !customSlot}
            loading={confirm.isPending}
            onClick={() => confirm.mutate()}
          >
            면담 확정
          </Button>
        </>
      )}

      {session.confirmedAt && session.status !== 'CANCELED' && (
        <p className="mb-3 text-base font-semibold">{slotLabel(session.confirmedAt)}</p>
      )}

      {(session.status === 'CONFIRMED' || session.status === 'DONE') && (
        <div className="flex flex-col gap-3">
          <Field label="비공개 메모 (교사용)" hint="가정에는 보이지 않아요.">
            <Textarea
              rows={4}
              value={privateNote}
              onChange={(e) => setPrivateNote(e.target.value)}
              placeholder="면담에서 나눈 이야기를 자유롭게 남겨 두세요."
            />
          </Field>

          <div>
            <Field label="공개 요약 (학부모 열람)">
              <Textarea
                rows={4}
                value={publicSummary}
                onChange={(e) => setPublicSummary(e.target.value)}
                placeholder="가정에 전할 이야기를 담백하게 정리해 주세요."
              />
            </Field>

            {aiStatus.data?.available && (
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  icon={<Sparkles size={14} />}
                  loading={ai.isPending && ai.variables === 'DRAFT'}
                  onClick={() => ai.mutate('DRAFT')}
                >
                  AI 초안
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!publicSummary.trim()}
                  loading={ai.isPending && ai.variables === 'POLISH'}
                  onClick={() => ai.mutate('POLISH')}
                >
                  다듬기
                </Button>
                <span className="ml-auto text-sm text-ink-sub tnum">
                  오늘 {aiStatus.data.usedToday}/{aiStatus.data.dailyLimit}회
                </span>
              </div>
            )}
            {aiAssisted && (
              <p className="mt-1.5 text-sm text-ink-sub">
                AI 초안이 담겼어요. 면담 메모만 근거로 하니, 사실과 다른 부분은 꼭 고쳐 주세요.
              </p>
            )}
          </div>

          {warnings.length > 0 && (
            <div className="rounded-sm border border-secondary px-3 py-2.5 text-sm">
              <p className="mb-1.5 flex items-center gap-1.5 font-semibold">
                <CircleAlert size={15} /> 살펴볼 표현이 있어요
              </p>
              <ul className="flex flex-col gap-1.5">
                {warnings.map((warning, index) => (
                  <li key={`${warning.term}-${index}`}>
                    <b>“{warning.term}”</b> — 제안: {warning.suggestions.join(' / ')}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {message && (
            <p className="rounded-sm bg-primary-soft px-3 py-2 text-sm text-primary-strong">
              {message}
            </p>
          )}

          <Button block loading={save.isPending} onClick={handleSave}>
            면담록 저장
          </Button>
        </div>
      )}

      {error && (
        <p role="alert" className="mt-2 rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
          {error}
        </p>
      )}
    </Card>
  );
}
